<template>
  <div class="anamnesis-container">
    <Breadcrumbs />
    <div class="page-header">
      <h1 class="page-title">Mes Collections</h1>
      <p class="page-subtitle">Organisez vos mémos en créant des collections thématiques.</p>
    </div>

    <CollectionSubset
      :items="collections"
      @create="handleCreate"
      @view="handleView"
      @edit="handleEdit"
      @delete="handleDelete"
    />

    <!-- Modale de création/édition -->
    <CollectionEditor
      v-model="showEditorModal"
      :collection-to-edit="selectedCollection"
      @collection-saved="handleCollectionSaved"
    />
    
    <!-- Modale de confirmation de suppression -->
    <ModalAreYouSure
        v-model="showDeleteConfirmation"
        title="Confirmer la suppression"
        :message="`Voulez-vous vraiment supprimer la collection '${collectionToDelete?.name}' ? Cette action est irréversible.`"
        confirm-text="Supprimer"
        @confirm="confirmDelete"
    />
  </div>
</template>

<script setup lang="ts">
import type { MemoCollection } from '@/api/types/memos/collections';
import { useBreadcrumbsStore } from '@/stores/breadcrumbs';
import { useCollectionsStore } from '@/stores/collections';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

import Breadcrumbs from '@/components/Breadcrumbs.vue';
import CollectionEditor from '@/components/collections/CollectionEditor.vue'; // Sera créé ensuite
import CollectionSubset from '@/components/subsets/CollectionSubset.vue';
import ModalAreYouSure from '@/components/utils/modal_areyousure.vue';

const router = useRouter();
const collectionsStore = useCollectionsStore();
const breadcrumbsStore = useBreadcrumbsStore();
const toast = useToast();

const { collections, getError: error } = storeToRefs(collectionsStore);

const showEditorModal = ref(false);
const selectedCollection = ref<MemoCollection | null>(null);

const showDeleteConfirmation = ref(false);
const collectionToDelete = ref<MemoCollection | null>(null);

breadcrumbsStore.updateBreadcrumbs([{ name: 'Mes Collections', path: '/collections' }]);

onMounted(() => {
  collectionsStore.fetchCollections();
});

const handleCreate = () => {
  selectedCollection.value = null;
  showEditorModal.value = true;
};

const handleView = (collection: MemoCollection) => {
  router.push({ name: 'CollectionView', params: { id: collection.uuid } });
};

const handleEdit = (collection: MemoCollection) => {
  selectedCollection.value = collection;
  showEditorModal.value = true;
};

const handleDelete = (collection: MemoCollection) => {
    collectionToDelete.value = collection;
    showDeleteConfirmation.value = true;
};

const confirmDelete = async () => {
    if (collectionToDelete.value) {
        const success = await collectionsStore.deleteCollection(collectionToDelete.value.uuid);
        if (success) {
            toast.success(`La collection '${collectionToDelete.value.name}' a été supprimée.`);
        } else {
            toast.error(error.value || `Erreur lors de la suppression de la collection.`);
        }
    }
    showDeleteConfirmation.value = false;
    collectionToDelete.value = null;
};

const handleCollectionSaved = () => {
  showEditorModal.value = false;
  // Le cache est déjà mis à jour dans CollectionEditor, pas besoin de recharger
};
</script>

<style scoped>
.page-header {
  margin-bottom: 1.5rem;
}
.page-title {
  font-size: 1.875rem; /* text-3xl */
  font-weight: 700;
  color: #1f2937; /* gray-800 */
}
.page-subtitle {
    margin-top: 0.5rem;
    font-size: 1rem;
    color: #6b7280; /* gray-500 */
}
</style> 